import { hashSeed } from "../rng.js";
import { npcGreeting, npcLine, type NpcDef } from "../npcs.js";
import type { DialogueSpan } from "./dialogue.js";
import { npcPersonalityDialogue } from "./personalityDialogue.js";

export type NpcDialogueChoice =
  | { id: string; label: string; kind: "dialogue"; node: string }
  | { id: string; label: string; kind: "panel"; panel: "shop" }
  | { id: string; label: string; kind: "close" };
export interface NpcDialogueNode {
  id: string; text: string; spans?: DialogueSpan[]; choices: NpcDialogueChoice[];
}
export interface NpcShopOffer { id: string; itemId: string; price: number }
export function npcProfession(npc: Pick<NpcDef, "id" | "role">): string {
  return npc.role === "trader" && hashSeed(npc.id) % 3 === 0 ? "baker" : npc.role;
}
/** Host-owned capabilities. Text and cosmetic performance cannot add stock or prices. */
export function npcShopOffers(npc: Pick<NpcDef, "id" | "role">): NpcShopOffer[] {
  if (npc.role !== "trader") return [];
  return [
    { id: "bread", itemId: "bread", price: 6 },
    { id: "meal", itemId: "hearty-meal", price: 12 },
    ...(npcProfession(npc) === "baker" ? [] : [{ id: "medkit", itemId: "medkit", price: 28 }]),
  ];
}
const back: NpcDialogueChoice = { id: "back", label: "Something else…", kind: "dialogue", node: "root" };
const work: NpcDialogueChoice = { id: "work", label: "Tell me about your work.", kind: "dialogue", node: "work" };
const interests: NpcDialogueChoice = { id: "interests", label: "What do you enjoy?", kind: "dialogue", node: "interests" };
const values: NpcDialogueChoice = { id: "values", label: "What matters to you?", kind: "dialogue", node: "values" };
const shop: NpcDialogueChoice = { id: "shop", label: "Let me see what you have.", kind: "panel", panel: "shop" };
const leave: NpcDialogueChoice = { id: "leave", label: "I'll let you get back to it.", kind: "close" };

/** Small authored tree, with explicit transitions and capability-checked panel choices.
 * A future dialogue provider can supply nodes without owning purchases or quest actions. */
export function npcDialogueNode(npc: NpcDef, node: string, context: {
  townName: string; day: number; tier?: number; salt?: number; greeting?: string | null;
}): NpcDialogueNode {
  const hasShop = npcShopOffers(npc).length > 0, baker = npcProfession(npc) === "baker";
  if (npc.personality && (node === "interests" || node === "values")) {
    const text = npcPersonalityDialogue(npc.personality, node === "interests" ? "interest" : "values");
    return { id: node, text, choices: [node === "interests" ? values : interests, work, back, leave] };
  }
  if (node === "chat") return { id: node,
    text: npcLine(npc, context.townName, context.tier ?? 0, context.salt ?? context.day), choices: [work, back, leave] };
  if (node === "work") {
    const text = baker ? "The trick is patience.\nGive the dough time, and it will tell you when it's ready." :
      npc.role === "artisan" ? "A repair should last. I take my time with the parts people never see. That's where good work begins." :
      npc.role === "trader" ? "I keep useful things close to hand. Have a look at the stock; the prices are marked." :
      npc.role === "warden" ? "I watch the road. A quiet watch is a good watch." :
      npc.role === "farmer" ? "You learn to pay attention to what is growing, and to what isn't." :
      "There's always something to attend to. For now, I have time to talk.";
    return { id: node, text, spans: [{ start: 0, end: text.indexOf(".") + 1, italic: true, pace: "slow" }],
      choices: [{ id: "advice", label: baker ? "What should I try?" : "Any advice for me?", kind: "dialogue", node: "advice" },
        ...(hasShop ? [shop] : []), back, leave] };
  }
  if (node === "advice") {
    const text = baker ? "Bread first. Simple, warm, and worth slowing down for." : npc.personality
      ? npcPersonalityDialogue(npc.personality, "advice") : "Start small. Pay attention. And keep the promises you make.";
    return { id: node, text, spans: [{ start: 0, end: text.indexOf(".") + 1, bold: true, size: "large", pause: 0.25 }],
      choices: [...(hasShop ? [shop] : []), back, leave] };
  }
  return { id: "root", text: context.greeting ?? npcGreeting(npc, context.townName, context.day),
    choices: [{ id: "chat", label: "How are things?", kind: "dialogue", node: "chat" }, work, ...(npc.personality ? [interests] : []),
      ...(hasShop ? [shop] : []), leave] };
}
